"use client";

/**
 * Notification popover for Scale App and Repos Without Access alerts.
 *
 * Data comes from the connectedGithubOrgs endpoint (hit_secondary_rate_limit,
 * next_scale_app, repos_without_access fields on each installation).
 * Only shown to org admins. Scale App notification requires hit_secondary_rate_limit.
 * Dismiss state is persisted to localStorage keyed by org (within its GitHub
 * instance) + content signature (resets automatically when the underlying data
 * changes).
 * Query params ?show_scale_notification=true / ?show_repos_notification=true force-show
 * dismissed notifications for testing/support.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { atom, useAtom } from "jotai";
import { AlertTriangle, Bell, Info, X } from "lucide-react";
import { cn } from "@repo/blacksmith-ui/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/blacksmith-ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { Badge } from "@repo/blacksmith-ui/badge";
import { Button } from "@repo/blacksmith-ui/button";
import { type GithubInstallations, useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { type NamedOrg, orgStorageKey } from "@/lib/named-org";
import { useLocalStorage } from "usehooks-ts";
const MAX_REPOS_DISPLAYED = 5;
const AUTO_OPEN_DELAY_MS = 500;

// Atom so auto-open only fires once per page load, even when the component
// remounts (e.g. sidebar collapse/expand swaps instances).
const hasAutoOpenedAtom = atom(false);
const SCALE_APP_INSTALL_URL = "https://github.com/apps/blacksmith-sh/installations/select_target";
type ScaleApp = NonNullable<GithubInstallations["installations"][number]["next_scale_app"]>;
interface NotificationFloatingButtonProps {
  icon?: React.ReactNode;
  className?: string;
  variant?: "floating" | "sidebar";
  /**
   * Sidebar variant only: renders the trigger as a full-width icon+label row
   * (used by the app-switcher rail, where labels are revealed on hover).
   */
  label?: string;
  expanded?: boolean;
  // Mirrors RailRowContents: the rail's animated width wipe keeps labels in
  // the DOM and clips them, while the icon-collapsible sidebar removes them
  // outright when collapsed.
  hideLabelWhenCollapsed?: boolean;
}
const SIDEBAR_ICON_BUTTON_CLASS = "h-8 w-8 rounded-lg p-1.5 text-neutral-500 hover:bg-black/[0.04] hover:text-neutral-900 hover:!border-transparent dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white dark:hover:!border-transparent cursor-pointer shrink-0 data-[state=open]:!bg-[#0000000f] data-[state=open]:text-neutral-900 data-[state=open]:!border-transparent dark:data-[state=open]:!bg-white/6 dark:data-[state=open]:text-white";

// Mirrors the app-switcher rail nav rows (RAIL_ROW in AppSwitcherRail): the
// darker resting text/icon of a nav item, a hover fill + color, and an
// open-state fill while the popover is open. `border-0` cancels the subtle
// Button variant's resting 2px transparent border so the row height and icon
// centering match the neighboring nav rows exactly.
const SIDEBAR_RAIL_ROW_CLASS = "rail-row flex h-8 w-full cursor-pointer items-center justify-start gap-0 overflow-hidden rounded-md border-0 p-0 transition-none type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] data-[state=open]:!bg-[#0000000f] data-[state=open]:text-neutral-950 data-[state=open]:[&_svg]:text-neutral-900 dark:data-[state=open]:!bg-white/6 dark:data-[state=open]:text-white dark:data-[state=open]:[&_svg]:text-white";
const DISMISS_BUTTON_CLASS = "rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-white";
const CARD_CLASS = "relative rounded-lg border border-black/10 p-3 text-sm bg-white text-neutral-900 dark:border-white/10 dark:bg-[#2A2C2C] dark:text-white";
const ORG_BADGE_CLASS = "rounded-md border-black/10 bg-neutral-100 font-mono text-neutral-800 dark:border-white/10 dark:bg-neutral-800 dark:text-white";

// Self-end alignment layered onto the secondary Button pill.
const ACTION_LINK_CLASS = "self-end border-black/[0.24] bg-transparent text-neutral-900 hover:border-black/[0.24] hover:bg-neutral-100 dark:border-white/[0.24] dark:text-white dark:hover:border-white/[0.24] dark:hover:bg-white/10";
function NotificationBadge(t0) {
  const $ = _c(10);
  const {
    count,
    size
  } = t0;
  const label = count > 99 ? "99+" : String(count);
  const isSm = size === "sm";
  const wrapperClass = isSm ? "absolute -top-1 -right-1 flex h-[14px] w-[14px] overflow-visible" : "absolute -top-1.5 -right-1.5 flex h-[18px] w-[18px] overflow-visible";
  const badgeClass = isSm ? "min-w-[14px] h-[14px] rounded-full text-[9px]" : "min-w-[18px] h-[18px] rounded-full text-[11px]";
  let t1;
  if ($[0] !== wrapperClass) {
    t1 = cn(wrapperClass, "items-center justify-center");
    $[0] = wrapperClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== badgeClass) {
    t2 = cn("relative flex items-center justify-center rounded-full font-medium", "bg-[#202122] text-white dark:bg-white dark:text-[#202122] dark:border dark:border-[#242526]", badgeClass);
    $[2] = badgeClass;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label || $[5] !== t2) {
    t3 = <span className={t2}>{label}</span>;
    $[4] = label;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <span className={t1}>{t3}</span>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// Countless dot indicator for the rail bell. Positioned over the bell's
// upper-right, sitting well inside the 32px icon slot so the sidebar button's
// overflow-hidden never clips it. Fades out once the rail panel expands (the
// panel is a `group` with data-state), where the pill count takes over.
function NotificationDot() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="pointer-events-none absolute right-[6px] top-[5px] size-[9px] rounded-full border border-sidebar bg-[#202122] transition-opacity duration-200 group-data-[state=expanded]:opacity-0 dark:bg-white" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function NotificationCard(t0) {
  const $ = _c(17);
  const {
    onDismiss,
    icon: Icon,
    iconClassName,
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("absolute right-2 top-2", DISMISS_BUTTON_CLASS);
    t2 = <X className="size-4" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onDismiss) {
    t3 = <button type="button" onClick={onDismiss} aria-label="Dismiss notification" className={t1}>{t2}</button>;
    $[2] = onDismiss;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== iconClassName) {
    t4 = cn("size-3.5 mt-[0.2em] shrink-0", iconClassName);
    $[4] = iconClassName;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== Icon || $[7] !== t4) {
    t5 = <Icon className={t4} aria-hidden={true} />;
    $[6] = Icon;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== children) {
    t6 = <div className="flex min-w-0 flex-1 flex-col gap-1.5">{children}</div>;
    $[9] = children;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex items-start gap-1.5">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <div className={CARD_CLASS}>{t3}{t7}</div>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
type BellProps = Required<Pick<NotificationFloatingButtonProps, "variant" | "expanded" | "hideLabelWhenCollapsed">> & Pick<NotificationFloatingButtonProps, "icon" | "className" | "label">;
function popoverSideFor(variant: BellProps["variant"]) {
  return variant === "sidebar" ? "right" : "top";
}
function renderBell({
  icon,
  className,
  variant,
  label,
  expanded,
  hideLabelWhenCollapsed,
  count
}: BellProps & {
  count: number;
}) {
  const isSidebar = variant === "sidebar";
  return isSidebar ? label ?
  // Button is the direct trigger child (no wrapper) so PopoverTrigger's
  // data-state="open" lands on it and SIDEBAR_RAIL_ROW_CLASS's
  // data-[state=open] active styling fires while the popover is open.
  <Button variant="subtle"
  // Use aria-disabled (not the disabled attribute) when empty: the
  // Button's disabled styling greys the icon/label, but we want the row
  // to read at full strength and keep its hover state even when there
  // are no notifications (clicking is simply a no-op — the empty state
  // renders inside a Tooltip rather than the Popover).
  aria-disabled={count === 0} className={cn(SIDEBAR_RAIL_ROW_CLASS, className)} aria-label="Notifications">
        <span className="relative flex size-8 shrink-0 items-center justify-center">
          <Bell className="h-[18px] w-[18px]" />
          {count > 0 && <NotificationDot />}
        </span>
        <span className={cn("type-body-s-regular whitespace-nowrap text-left", hideLabelWhenCollapsed && !expanded && "hidden")}>
          {label}
        </span>
        {/* Right-aligned count pill (revealed with the label when the rail
              is expanded; clipped by overflow-hidden while collapsed). Shown
              only when there is at least one notification; matches the mini
              dot's fill/stroke. */}
        {count > 0 && !(hideLabelWhenCollapsed && !expanded) && <span className="ml-auto mr-3 inline-flex h-[16px] min-w-[16px] shrink-0 items-center justify-center rounded-full border border-sidebar bg-[#202122] px-1 text-[11px] font-medium leading-none text-white dark:bg-white dark:text-[#202122]">
            {count}
          </span>}
      </Button> : <Button variant="subtle" size="icon" aria-disabled={count === 0} className={cn(SIDEBAR_ICON_BUTTON_CLASS, "relative", count === 0 && "pointer-events-none", className)} aria-label="Notifications">
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && <NotificationDot />}
      </Button> : <div role="button" tabIndex={count > 0 ? 0 : -1} aria-label="Notifications" aria-disabled={count === 0} className={cn("fixed bottom-4 right-4 z-50 flex items-center p-2", "bg-white dark:bg-[#242526] border border-black/[0.24] rounded-full", "shadow-[0px_11px_12px_0px_rgba(0,0,0,0.15)]", count > 0 ? "cursor-pointer" : "opacity-50 pointer-events-none", className)}>
      <span className={cn("relative flex h-8 w-8 items-center justify-center shrink-0 rounded-full border border-neutral-900", "bg-blacksmith-yellow transition-colors hover:brightness-95")}>
        {icon ?? <Bell className="size-4 text-[#202122]" />}
        {count > 0 && <NotificationBadge count={count} size="md" />}
      </span>
    </div>;
}
function EmptyNotificationBell(t0) {
  const $ = _c(22);
  let T0;
  let T1;
  let popoverSide;
  let t1;
  let t2;
  let trigger;
  if ($[0] !== t0) {
    const {
      variant: t3,
      expanded: t4,
      hideLabelWhenCollapsed: t5,
      ...bell
    } = t0;
    const variant = t3 === undefined ? "floating" : t3;
    const expanded = t4 === undefined ? true : t4;
    const hideLabelWhenCollapsed = t5 === undefined ? false : t5;
    let t6;
    if ($[7] !== variant) {
      t6 = popoverSideFor(variant);
      $[7] = variant;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    popoverSide = t6;
    trigger = renderBell({
      ...bell,
      variant,
      expanded,
      hideLabelWhenCollapsed,
      count: 0
    });
    T1 = Tooltip;
    T0 = TooltipTrigger;
    t2 = true;
    t1 = cn("inline-flex", bell.label && "w-full");
    $[0] = t0;
    $[1] = T0;
    $[2] = T1;
    $[3] = popoverSide;
    $[4] = t1;
    $[5] = t2;
    $[6] = trigger;
  } else {
    T0 = $[1];
    T1 = $[2];
    popoverSide = $[3];
    t1 = $[4];
    t2 = $[5];
    trigger = $[6];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== trigger) {
    t3 = <span className={t1}>{trigger}</span>;
    $[9] = t1;
    $[10] = trigger;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== T0 || $[13] !== t2 || $[14] !== t3) {
    t4 = <T0 asChild={t2}>{t3}</T0>;
    $[12] = T0;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== popoverSide) {
    t5 = <TooltipContent side={popoverSide}>No new notifications</TooltipContent>;
    $[16] = popoverSide;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== T1 || $[19] !== t4 || $[20] !== t5) {
    t6 = <T1>{t4}{t5}</T1>;
    $[18] = T1;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}
export function NotificationFloatingButton(props) {
  const $ = _c(9);
  const {
    activeNamedOrg,
    isOrgAdmin,
    activeGithubOrgHitSecondaryRateLimit,
    activeGithubOrgNextScaleApp,
    activeGithubOrgReposWithoutAccess
  } = useActiveGithubOrgForUserNonSuspense();
  if (!activeNamedOrg) {
    let t0;
    if ($[0] !== props) {
      t0 = <EmptyNotificationBell {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== activeGithubOrgHitSecondaryRateLimit || $[3] !== activeGithubOrgNextScaleApp || $[4] !== activeGithubOrgReposWithoutAccess || $[5] !== activeNamedOrg || $[6] !== isOrgAdmin || $[7] !== props) {
    t0 = <OrgNotificationFloatingButton {...props} org={activeNamedOrg} isOrgAdmin={isOrgAdmin} hitSecondaryRateLimit={activeGithubOrgHitSecondaryRateLimit} nextScaleApp={activeGithubOrgNextScaleApp} reposWithoutAccess={activeGithubOrgReposWithoutAccess} />;
    $[2] = activeGithubOrgHitSecondaryRateLimit;
    $[3] = activeGithubOrgNextScaleApp;
    $[4] = activeGithubOrgReposWithoutAccess;
    $[5] = activeNamedOrg;
    $[6] = isOrgAdmin;
    $[7] = props;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  return t0;
}
interface OrgNotificationFloatingButtonProps extends NotificationFloatingButtonProps {
  org: NamedOrg;
  isOrgAdmin: boolean;
  hitSecondaryRateLimit: boolean;
  nextScaleApp: ScaleApp | null;
  reposWithoutAccess: string[] | null;
}
export function OrgNotificationFloatingButton(t0) {
  const $ = _c(96);
  const {
    org,
    isOrgAdmin,
    hitSecondaryRateLimit,
    nextScaleApp,
    reposWithoutAccess,
    icon,
    className,
    variant: t1,
    label,
    expanded: t2,
    hideLabelWhenCollapsed: t3
  } = t0;
  const variant = t1 === undefined ? "floating" : t1;
  const expanded = t2 === undefined ? true : t2;
  const hideLabelWhenCollapsed = t3 === undefined ? false : t3;
  const repos = reposWithoutAccess ?? [];
  const displayedRepos = repos.slice(0, MAX_REPOS_DISPLAYED);
  const remainingCount = repos.length - MAX_REPOS_DISPLAYED;
  const displayOrg = org.orgName;
  const scaleAppInstallUrl = nextScaleApp?.install_url ?? SCALE_APP_INSTALL_URL;
  let t4;
  if ($[0] !== nextScaleApp?.name) {
    t4 = nextScaleApp?.name.replace("scale", "") ?? "";
    $[0] = nextScaleApp?.name;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const scaleAppName = t4;
  let forceShowScale;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    forceShowScale = searchParams.get("show_scale_notification") === "true";
    t5 = searchParams.get("show_repos_notification");
    $[2] = forceShowScale;
    $[3] = t5;
  } else {
    forceShowScale = $[2];
    t5 = $[3];
  }
  const forceShowRepos = t5 === "true";
  const scaleAppContentKey = nextScaleApp?.name ?? "";
  let t6;
  if ($[4] !== reposWithoutAccess) {
    t6 = reposWithoutAccess ? reposWithoutAccess.slice().sort().join(",") : "";
    $[4] = reposWithoutAccess;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const reposContentKey = t6;
  let t7;
  if ($[6] !== org) {
    t7 = orgStorageKey("scale-app-notif-dismissed-", org);
    $[6] = org;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const [scaleAppDismissed, setScaleAppDismissed] = useLocalStorage(`${t7}-${scaleAppContentKey}`, false);
  let t8;
  if ($[8] !== org) {
    t8 = orgStorageKey("repos-without-access-notif-dismissed-", org);
    $[8] = org;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const [reposDismissed, setReposDismissed] = useLocalStorage(`${t8}-${reposContentKey}`, false);
  const [open, setOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useAtom(hasAutoOpenedAtom);
  const focusTargetRef = useRef(null);
  let t9;
  if ($[10] !== setReposDismissed || $[11] !== setScaleAppDismissed) {
    t9 = id => {
      if (id === "scale-app") {
        setScaleAppDismissed(true);
      }
      if (id === "repos-without-access") {
        setReposDismissed(true);
      }
    };
    $[10] = setReposDismissed;
    $[11] = setScaleAppDismissed;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const dismiss = t9;
  let t10;
  if ($[13] !== setOpen || $[14] !== setReposDismissed || $[15] !== setScaleAppDismissed) {
    t10 = () => {
      setScaleAppDismissed(true);
      setReposDismissed(true);
      setOpen(false);
    };
    $[13] = setOpen;
    $[14] = setReposDismissed;
    $[15] = setScaleAppDismissed;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const clearAll = t10;
  const scaleAppEligible = isOrgAdmin && nextScaleApp !== null && hitSecondaryRateLimit;
  const reposEligible = isOrgAdmin && repos.length > 0;
  const showScaleApp = !scaleAppDismissed && (forceShowScale || scaleAppEligible);
  const showReposWithoutAccess = !reposDismissed && (forceShowRepos || reposEligible);
  const notificationCount = (showScaleApp ? 1 : 0) + (showReposWithoutAccess ? 1 : 0);
  const hasNotifications = notificationCount > 0;
  if (open && !hasNotifications) {
    setOpen(false);
  }
  let t11;
  if ($[17] !== hasAutoOpened || $[18] !== hasNotifications || $[19] !== setHasAutoOpened || $[20] !== setOpen || $[21] !== variant) {
    t11 = () => {
      if (variant !== "sidebar" || !hasNotifications || hasAutoOpened) {
        return;
      }
      const openPopover = () => {
        setHasAutoOpened(true);
        setOpen(true);
      };
      let timer;
      const schedule = () => {
        timer = setTimeout(openPopover, AUTO_OPEN_DELAY_MS);
      };
      if (typeof document !== "undefined" && document.readyState === "complete") {
        schedule();
      } else {
        if (typeof window !== "undefined") {
          window.addEventListener("load", schedule);
          return () => {
            window.removeEventListener("load", schedule);
            clearTimeout(timer);
          };
        }
      }
      return () => clearTimeout(timer);
    };
    $[17] = hasAutoOpened;
    $[18] = hasNotifications;
    $[19] = setHasAutoOpened;
    $[20] = setOpen;
    $[21] = variant;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== hasAutoOpened || $[24] !== hasNotifications || $[25] !== setHasAutoOpened || $[26] !== variant) {
    t12 = [variant, hasNotifications, hasAutoOpened, setHasAutoOpened];
    $[23] = hasAutoOpened;
    $[24] = hasNotifications;
    $[25] = setHasAutoOpened;
    $[26] = variant;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  useEffect(t11, t12);
  let isSidebar;
  let popoverAlign;
  let popoverAlignOffset;
  let popoverSide;
  let popoverSideOffset;
  let t13;
  let t14;
  if ($[28] !== className || $[29] !== expanded || $[30] !== hasNotifications || $[31] !== hideLabelWhenCollapsed || $[32] !== icon || $[33] !== label || $[34] !== notificationCount || $[35] !== variant) {
    t14 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const bell = {
        icon,
        className,
        variant,
        label,
        expanded,
        hideLabelWhenCollapsed
      };
      if (!hasNotifications) {
        t14 = <EmptyNotificationBell {...bell} />;
        break bb0;
      }
      isSidebar = variant === "sidebar";
      popoverAlign = isSidebar ? "start" : "end";
      let t15;
      if ($[43] !== variant) {
        t15 = popoverSideFor(variant);
        $[43] = variant;
        $[44] = t15;
      } else {
        t15 = $[44];
      }
      popoverSide = t15;
      popoverSideOffset = isSidebar ? 8 : 16;
      popoverAlignOffset = isSidebar ? -8 : 0;
      t13 = renderBell({
        ...bell,
        count: notificationCount
      });
    }
    $[28] = className;
    $[29] = expanded;
    $[30] = hasNotifications;
    $[31] = hideLabelWhenCollapsed;
    $[32] = icon;
    $[33] = label;
    $[34] = notificationCount;
    $[35] = variant;
    $[36] = isSidebar;
    $[37] = popoverAlign;
    $[38] = popoverAlignOffset;
    $[39] = popoverSide;
    $[40] = popoverSideOffset;
    $[41] = t13;
    $[42] = t14;
  } else {
    isSidebar = $[36];
    popoverAlign = $[37];
    popoverAlignOffset = $[38];
    popoverSide = $[39];
    popoverSideOffset = $[40];
    t13 = $[41];
    t14 = $[42];
  }
  if (t14 !== Symbol.for("react.early_return_sentinel")) {
    return t14;
  }
  const trigger = t13;
  const t15 = isSidebar ? "animate-notification-popover-in-right" : "animate-notification-popover-in";
  let t16;
  if ($[45] !== t15) {
    t16 = cn("w-96 max-w-[calc(100vw-2rem)] p-0 outline-hidden ring-0 rounded-lg", "before:absolute before:bg-transparent before:content-['']", "data-[side=right]:before:-left-3 data-[side=right]:before:top-0 data-[side=right]:before:h-full data-[side=right]:before:w-3", "data-[side=left]:before:-right-3 data-[side=left]:before:top-0 data-[side=left]:before:h-full data-[side=left]:before:w-3", "data-[side=top]:before:-bottom-3 data-[side=top]:before:left-0 data-[side=top]:before:h-3 data-[side=top]:before:w-full", "data-[side=bottom]:before:-top-3 data-[side=bottom]:before:left-0 data-[side=bottom]:before:h-3 data-[side=bottom]:before:w-full", t15);
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const popoverClass = t16;
  const headerPadding = isSidebar ? "pl-3 pr-1.5 py-1.5" : "pl-2 pr-1.5 py-2";
  const showCollapsedLabelTooltip = isSidebar && !!label && hideLabelWhenCollapsed && !expanded;
  const T0 = Popover;
  const t17 = false;
  let t18;
  if ($[47] !== label || $[48] !== popoverSide || $[49] !== showCollapsedLabelTooltip || $[50] !== trigger) {
    t18 = showCollapsedLabelTooltip ? <Tooltip><TooltipTrigger asChild={true}><PopoverTrigger asChild={true}>{trigger}</PopoverTrigger></TooltipTrigger><TooltipContent side={popoverSide}>{label}</TooltipContent></Tooltip> : <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>;
    $[47] = label;
    $[48] = popoverSide;
    $[49] = showCollapsedLabelTooltip;
    $[50] = trigger;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  const T1 = PopoverContent;
  let t19;
  let t20;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e => {
      e.preventDefault();
      focusTargetRef.current?.focus();
    };
    t20 = <span ref={focusTargetRef} tabIndex={-1} className="sr-only">Notifications</span>;
    $[52] = t19;
    $[53] = t20;
  } else {
    t19 = $[52];
    t20 = $[53];
  }
  let t21;
  if ($[54] !== headerPadding) {
    t21 = cn("flex items-center justify-between gap-2 border-b border-black/10 dark:border-white/10", headerPadding);
    $[54] = headerPadding;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <h3 className="font-semibold text-sm text-neutral-900 dark:text-white">Notifications</h3>;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== clearAll) {
    t23 = <button type="button" onClick={clearAll} className="text-xs font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white">Clear all</button>;
    $[57] = clearAll;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== setOpen) {
    t24 = () => setOpen(false);
    $[59] = setOpen;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <X className="size-4" />;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t24) {
    t26 = <button type="button" onClick={t24} aria-label="Close notifications" className={DISMISS_BUTTON_CLASS}>{t25}</button>;
    $[62] = t24;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t23 || $[65] !== t26) {
    t27 = <div className="flex items-center gap-1">{t23}{t26}</div>;
    $[64] = t23;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== t21 || $[68] !== t27) {
    t28 = <div className={t21}>{t22}{t27}</div>;
    $[67] = t21;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  const t29 = "flex flex-col gap-1.5 p-2 max-h-[400px] overflow-y-auto";
  let t30;
  if ($[70] !== dismiss || $[71] !== displayOrg || $[72] !== scaleAppInstallUrl || $[73] !== scaleAppName || $[74] !== showScaleApp) {
    t30 = showScaleApp && <NotificationCard onDismiss={() => dismiss("scale-app")} icon={AlertTriangle} iconClassName="text-amber-600 dark:text-amber-400"><p className="pr-8">Your GitHub organization{" "}<Badge variant="muted" className={ORG_BADGE_CLASS}>{displayOrg}</Badge>{" "}is hitting GitHub API rate limits, which can slow down runner provisioning. Install Blacksmith Scale App{scaleAppName ? ` ${scaleAppName}` : ""} to resolve this.</p><Button asChild={true} variant="secondary" className={ACTION_LINK_CLASS}><a href={scaleAppInstallUrl} target="_blank" rel="noreferrer">Install</a></Button></NotificationCard>;
    $[70] = dismiss;
    $[71] = displayOrg;
    $[72] = scaleAppInstallUrl;
    $[73] = scaleAppName;
    $[74] = showScaleApp;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  const t31 = showReposWithoutAccess && <NotificationCard onDismiss={() => dismiss("repos-without-access")} icon={Info} iconClassName="text-blue-600 dark:text-blue-400"><p className="pr-8">Blacksmith is running jobs for{" "}<Badge variant="muted" className={ORG_BADGE_CLASS}>{displayOrg}</Badge>{" "}on {repos.length}{" "}{repos.length === 1 ? "repository" : "repositories"} it isn't receiving GitHub events for, which can delay job pickup. Check that the Blacksmith GitHub App has access to{repos.length > 0 ? <>{" "}<strong>{displayedRepos.join(", ")}</strong>{remainingCount > 0 && ` + ${remainingCount} more`}.</> : " these repositories."}</p><Button asChild={true} variant="secondary" className={ACTION_LINK_CLASS}><a href={SCALE_APP_INSTALL_URL} target="_blank" rel="noreferrer">Review access</a></Button></NotificationCard>;
  let t32;
  if ($[76] !== t30 || $[77] !== t31) {
    t32 = <div className={t29}>{t30}{t31}</div>;
    $[76] = t30;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== T1 || $[80] !== popoverAlign || $[81] !== popoverAlignOffset || $[82] !== popoverClass || $[83] !== popoverSide || $[84] !== popoverSideOffset || $[85] !== t19 || $[86] !== t20 || $[87] !== t28 || $[88] !== t32) {
    t33 = <T1 align={popoverAlign} side={popoverSide} sideOffset={popoverSideOffset} alignOffset={popoverAlignOffset} className={popoverClass} onOpenAutoFocus={t19}>{t20}{t28}{t32}</T1>;
    $[79] = T1;
    $[80] = popoverAlign;
    $[81] = popoverAlignOffset;
    $[82] = popoverClass;
    $[83] = popoverSide;
    $[84] = popoverSideOffset;
    $[85] = t19;
    $[86] = t20;
    $[87] = t28;
    $[88] = t32;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== T0 || $[91] !== open || $[92] !== setOpen || $[93] !== t18 || $[94] !== t33) {
    t34 = <T0 open={open} onOpenChange={setOpen} modal={t17}>{t18}{t33}</T0>;
    $[90] = T0;
    $[91] = open;
    $[92] = setOpen;
    $[93] = t18;
    $[94] = t33;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  return t34;
}